import React, { useState, useRef, useEffect } from 'react';
import { Row, Col, Form, InputGroup } from 'react-bootstrap';
import { restrictToAlphabets } from '../../utils/inputHelpers';
import styled from 'styled-components';
import { BiSearch, BiChevronDown } from 'react-icons/bi';

const StyledFormGroup = styled(Form.Group)`
  margin-bottom: 1.25rem;

  .form-label {
    font-weight: 500;
    color: #495057;
    margin-bottom: 0.5rem;
  }

  .form-control, .form-select {
    border-radius: 8px;
    padding: 0.5rem 0.75rem;
    border: 1px solid #e0e0e0;
    transition: all 0.3s ease;

    &:focus {
      border-color: #4b6cb7;
      box-shadow: 0 0 0 0.25rem rgba(75, 108, 183, 0.25);
    }

    &.is-invalid {
      border-color: #dc3545;
    }
  }
`;

const GenderDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-top: 0.25rem;
`;

const GenderOption = styled.div`
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const NoResults = styled.div`
  padding: 0.75rem 1rem;
  color: #6c757d;
  font-style: italic;
`;

const genderOptions = ["Male", "Female", "Other"];

const BasicInformation = ({ register, errors }) => {
  const [genderSearch, setGenderSearch] = useState('');
  const [showGenderDropdown, setShowGenderDropdown] = useState(false);
  const [selectedGender, setSelectedGender] = useState('');
  const genderInputRef = useRef(null);
  const dropdownRef = useRef(null);

  const { onChange, onBlur, ref, ...genderField } = register('gender');

  const filteredGenders = genderOptions.filter(gender =>
    gender.toLowerCase().includes(genderSearch.toLowerCase())
  );

  const handleGenderSelect = (gender) => {
    setGenderSearch(gender);
    setSelectedGender(gender);
    setShowGenderDropdown(false);
    
    const event = {
      target: {
        name: 'gender',
        value: gender
      }
    };
    onChange(event);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target) && !genderInputRef.current.contains(event.target)) {
      setShowGenderDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <h5 className="section-title">Basic Information</h5>
      <Row>
        <StyledFormGroup as={Col} md="4" controlId="firstName">
          <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            {...register('firstName')}
            onKeyDown={restrictToAlphabets}
            onPaste={(e) => {
              const pastedText = e.clipboardData.getData('text');
              if (!/^[a-zA-Z\s]*$/.test(pastedText)) {
                e.preventDefault();
              }
            }}
            isInvalid={!!errors.firstName}
            placeholder="Enter first name"
          />
          <Form.Control.Feedback type="invalid" className="d-flex align-items-center">
            <i className="bi bi-exclamation-circle me-2"></i>
            {errors.firstName?.message}
          </Form.Control.Feedback>
        </StyledFormGroup>

        <StyledFormGroup as={Col} md="4" controlId="middleName">
          <Form.Label>Middle Name</Form.Label>
          <Form.Control
            type="text"
            {...register('middleName')}
            onKeyDown={restrictToAlphabets}
            onPaste={(e) => {
              const pastedText = e.clipboardData.getData('text');
              if (!/^[a-zA-Z\s]*$/.test(pastedText)) {
                e.preventDefault();
              }
            }}
            isInvalid={!!errors.middleName}
            placeholder="Enter middle name"
          />
          <Form.Control.Feedback type="invalid" className="d-flex align-items-center">
            <i className="bi bi-exclamation-circle me-2"></i>
            {errors.middleName?.message}
          </Form.Control.Feedback>
        </StyledFormGroup>

        <StyledFormGroup as={Col} md="4" controlId="lastName">
          <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            {...register('lastName')}
            onKeyDown={restrictToAlphabets}
            onPaste={(e) => {
              const pastedText = e.clipboardData.getData('text');
              if (!/^[a-zA-Z\s]*$/.test(pastedText)) {
                e.preventDefault();
              }
            }}
            isInvalid={!!errors.lastName}
            placeholder="Enter last name"
          />
          <Form.Control.Feedback type="invalid" className="d-flex align-items-center">
            <i className="bi bi-exclamation-circle me-2"></i>
            {errors.lastName?.message}
          </Form.Control.Feedback>
        </StyledFormGroup>
      </Row>

      <Row>
        <StyledFormGroup as={Col} md="4" controlId="birthDate">
          <Form.Label>Date of Birth <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="date"
            {...register('birthDate')}
            isInvalid={!!errors.birthDate}
          />
          <Form.Control.Feedback type="invalid" className="d-flex align-items-center">
            <i className="bi bi-exclamation-circle me-2"></i>
            {errors.birthDate?.message}
          </Form.Control.Feedback>
        </StyledFormGroup>

        <StyledFormGroup as={Col} md="4" controlId="gender">
          <Form.Label>Gender <span className="text-danger">*</span></Form.Label>
          <div style={{ position: 'relative' }} ref={dropdownRef}>
            <InputGroup>
              <Form.Control
                type="text"
                {...genderField}
                value={genderSearch}
                onChange={(e) => {
                  setGenderSearch(e.target.value);
                  onChange(e);
                  setShowGenderDropdown(true);
                }}
                onFocus={() => setShowGenderDropdown(true)}
                ref={(e) => {
                  ref(e);
                  genderInputRef.current = e;
                }}
                isInvalid={!!errors.gender}
                placeholder="Select gender"
                autoComplete="off"
              />
              <InputGroup.Text 
                style={{ cursor: 'pointer' }}
                onClick={() => setShowGenderDropdown(!showGenderDropdown)}
              >
                <BiChevronDown />
              </InputGroup.Text>
            </InputGroup>
            
            {showGenderDropdown && (
              <GenderDropdown>
                {filteredGenders.length > 0 ? (
                  filteredGenders.map((gender) => (
                    <GenderOption
                      key={gender}
                      onClick={() => handleGenderSelect(gender)}
                    >
                      {gender}
                    </GenderOption>
                  ))
                ) : (
                  <NoResults>
                    No gender options found matching "{genderSearch}"
                  </NoResults>
                )}
              </GenderDropdown>
            )}
            
            <Form.Control.Feedback type="invalid" className="d-flex align-items-center mt-1">
              <i className="bi bi-exclamation-circle me-2"></i>
              {errors.gender?.message}
            </Form.Control.Feedback>
          </div>
        </StyledFormGroup>
      </Row>
    </>
  );
};

export default BasicInformation;